/**
 * CR-07 submit-a-post popup and CR-08 `/submissions/:id`.
 *
 * The list lives on the campaign it belongs to. A new post is submitted
 * from that campaign as a popup, never from a standalone page.
 *
 * The screen never decides whether a link is acceptable. It sends what was
 * pasted and renders the server's answer, because the canonical id - the
 * thing the uniqueness rule keys on - is derived in the Worker and a
 * client that could influence it could choose whose post it collides with.
 *
 * The refusals are the part worth designing. "That post has already been
 * submitted" needs to say *why* an apparently different link counts as the
 * same post, or the creator concludes the platform is broken and submits
 * again. Same for a deadline, a used-up allowance, or a platform we cannot
 * measure - which reads "not supported", never "coming soon", because the
 * SRS is explicit that an unmeasurable route is not a route that is on its
 * way.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  Badge, Button, Card, Eyebrow, LinkButton, PageHeader, StateBlock,
} from "@ui/primitives";
import { TextInput, Checkbox, FormError } from "@ui/forms";
import { fontSize, space } from "@design/index";
import { api } from "@app/shared/api";
import { useToast } from "@app/shared/Toasts";
import { PageSkeleton, RibbonSkeleton } from "@app/shared/Skeletons";
import { PlatformIcon, platformMeta } from "@app/shared/Platforms";
import {
  missingRequiredPlatforms, platformSubmitStatuses, requiredSocialPlatforms,
  type ConnectedAccount,
} from "@server/submissionOwnership";
import { EarningStatusCard, formatViews, type EarningPreview } from "@app/shared/EarningClaim";
import { postOpenUrl } from "@server/platforms";

interface Submission {
  id: string; campaign_id: string; campaign_name: string | null;
  platform: string; post_id: string | null;
  submitted_url: string; canonical_url: string | null;
  state: string; moderation_state: string; availability: string;
  availability_checked_at: string | null;
  current_revision: number; submitted_at: string;
  decided_at: string | null; decision_note: string | null;
  earning?: EarningPreview | null;
}

interface Detail {
  submission: Submission;
  revisions: Array<{ revision: number; caption: string | null; notes: string | null; created_at: string }>;
  reviews: Array<{ revision: number; decision: string; reason: string | null; created_at: string }>;
}

const TONE: Record<string, "ok" | "warn" | "danger" | "process" | "neutral"> = {
  APPROVED: "ok", SUBMITTED: "process", PENDING_VERIFICATION: "process",
  REVISION_REQUESTED: "warn", REJECTED: "danger", WITHDRAWN: "neutral", REPLACED: "neutral",
};

function StateBadge({ value }: { value: string }) {
  return <Badge tone={TONE[value] ?? "neutral"}>{value.replaceAll("_", " ").toLowerCase()}</Badge>;
}

/** What the creator is told about the platform's own decision axis. */
function ModerationNote({ state }: { state: string }) {
  if (state === "CLEAR") return null;
  const text = state === "HELD"
    ? "This post is on hold under platform policy while we look at it. The brand's decision is separate and still stands."
    : state === "DISQUALIFIED"
      ? "This post has been disqualified under platform policy. You can appeal."
      : "This post has been flagged for a policy check. Nothing has changed about your submission yet.";
  return (
    <Card padding={16} style={{ borderColor: "var(--warn-line)" }}>
      <Eyebrow>Platform review</Eyebrow>
      <p style={{ margin: "6px 0 0", fontSize: fontSize.body, lineHeight: 1.6 }}>{text}</p>
    </Card>
  );
}

/* --------------------------------------------------------------- CR-07 */

export function SubmitPostDialog({
  open, onClose, onCreated, participationId, campaignName,
  requiredPlatforms = [], connections = [], submissions = [], submissionLimit = 1,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
  participationId: string;
  campaignName?: string;
  requiredPlatforms?: readonly string[];
  connections?: readonly ConnectedAccount[];
  submissions?: readonly { platform?: string | null; state?: string | null }[];
  submissionLimit?: number;
}) {
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [notes, setNotes] = useState("");
  const [disclosure, setDisclosure] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [platform, setPlatform] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    setUrl("");
    setCaption("");
    setNotes("");
    setDisclosure(false);
    setBusy(false);
    setError(null);
    setFields({});
    setPlatform(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  async function submit() {
    setBusy(true); setError(null); setFields({});
    const r = await api.post<{ submissionId: string }>("/submissions", {
      participationId, url: url.trim(), caption, notes, disclosure,
    });
    setBusy(false);
    if (r.ok) {
      toast.ok("Post submitted for review.");
      onCreated(r.data.submissionId);
      onClose();
      return;
    }
    if (r.fields) setFields(r.fields);
    else { setError(r.message); toast.error(r.message); }
  }

  if (!open) return null;

  const missing = missingRequiredPlatforms(requiredPlatforms, connections);
  const slots = platformSubmitStatuses({
    required: requiredPlatforms, connections, submissions, limit: submissionLimit,
  });
  const required = requiredSocialPlatforms(requiredPlatforms);
  const chosen = slots.find((s) => s.id === platform) ?? null;
  const blocked = chosen ? !chosen.canSubmit : required.length > 0 && !slots.some((s) => s.canSubmit);
  const missingNames = missing.map((id) => platformMeta(id).label).join(" and ");
  const chosenMeta = chosen ? platformMeta(chosen.id) : null;

  return (
    <div className="cm-dialog" onClick={() => { if (!busy) onClose(); }}>
      <div
        className="cm-dialog-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cm-submit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="cm-dialog-eyebrow">Submit</p>
        <h2 id="cm-submit-title" className="cm-dialog-title">
          {chosenMeta ? chosenMeta.label : "Which platform is this post on?"}
        </h2>
        <p className="cm-dialog-lead">
          {chosenMeta
            ? `Paste the public ${chosenMeta.label} link for ${campaignName ?? "this campaign"}.`
            : "Pick the platform, then paste the link for that post."}
        </p>

        <div style={{ display: "grid", gap: space(3) }}>
          {error ? <FormError message={error} /> : null}

          {required.length > 0 && !chosen ? (
            <div className="cm-platform-picks">
              {(slots.length > 0 ? slots : required.map((id) => ({
                id, connected: false, submitted: 0, limit: submissionLimit, canSubmit: false,
              }))).map((slot) => {
                const meta = platformMeta(slot.id);
                const note = !slot.connected
                  ? "Connect first"
                  : slot.submitted >= slot.limit
                    ? "Already sent"
                    : "Send a link";
                if (!slot.connected) {
                  return (
                    <a key={slot.id} className="cm-platform-pick" href="/social">
                      <img src={meta.src} alt="" />
                      <strong>{meta.label}</strong>
                      <span style={{ fontSize: 12, color: "var(--muted)" }}>{note}</span>
                    </a>
                  );
                }
                return (
                  <button
                    key={slot.id}
                    type="button"
                    className="cm-platform-pick"
                    disabled={!slot.canSubmit}
                    onClick={() => setPlatform(slot.id)}
                  >
                    <img src={meta.src} alt="" />
                    <strong>{meta.label}</strong>
                    <span style={{ fontSize: 12, color: "var(--muted)" }}>{note}</span>
                  </button>
                );
              })}
            </div>
          ) : null}

          {chosen && !chosen.connected ? (
            <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--text-2)", lineHeight: 1.55 }}>
              Connect {chosenMeta?.label ?? missingNames} under{" "}
              <a href="/social" style={{ color: "var(--accent)", fontWeight: 650 }}>Social accounts</a>
              {" "}before you submit.
            </p>
          ) : null}

          {required.length === 0 || chosen ? (
            <>
              <TextInput
                label={chosenMeta ? `${chosenMeta.label} link` : "Link to your published post"}
                required
                value={url}
                autoFocus
                onChange={(e) => setUrl(e.currentTarget.value)}
                error={fields["url"]}
                hint={chosenMeta
                  ? `Paste the public ${chosenMeta.label} post. The brand reviews the connected ${chosenMeta.label} account against this link.`
                  : "Paste the public post link. We match the username when the link includes one."}
              />
              <TextInput
                label="Caption"
                value={caption}
                onChange={(e) => setCaption(e.currentTarget.value)}
                hint="Optional. What you posted with it, for the brand's review."
              />
              <TextInput
                label="Notes for the brand"
                value={notes}
                onChange={(e) => setNotes(e.currentTarget.value)}
              />
              <Checkbox
                label="I have disclosed this as a paid partnership, as the brief requires."
                checked={disclosure}
                onChange={setDisclosure}
              />
              {fields["disclosure"] ? <FormError message={fields["disclosure"]} /> : null}
              <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--muted)", lineHeight: 1.6 }}>
                One post can be paid under one campaign. Submitting a post that is already claimed
                is refused rather than queued.
              </p>
            </>
          ) : null}

          <div className="cm-dialog-actions">
            {chosen && required.length > 0 ? (
              <Button variant="ghost" disabled={busy} onClick={() => setPlatform(null)}>Back</Button>
            ) : null}
            <Button variant="ghost" disabled={busy} onClick={onClose}>Cancel</Button>
            {required.length === 0 || chosen ? (
              <Button
                variant="primary"
                disabled={busy || blocked || !url.trim() || !participationId}
                onClick={() => void submit()}
              >
                {busy ? "Submitting…" : "Submit post"}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- CR-08 */

export function SubmissionDetailScreen({
  id, onBack,
}: { id: string; onBack: (campaignId?: string) => void }) {
  const [detail, setDetail] = useState<Detail | null | "error">(null);
  const [caption, setCaption] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    const r = await api.get<Detail>(`/submissions/${id}`);
    if (r.ok) {
      setDetail(r.data);
      const latest = r.data.revisions[0];
      setCaption(latest?.caption ?? "");
      setNotes(latest?.notes ?? "");
    } else setDetail("error");
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  async function act(path: string, body?: unknown) {
    setBusy(true); setError(null);
    const r = await api.post<{ ok: boolean }>(`/submissions/${id}/${path}`, body);
    setBusy(false);
    if (r.ok) {
      toast.ok(
        path === "withdraw" ? "Submission withdrawn."
          : path === "claim-earning" ? "Claim sent to the brand."
          : path === "refresh-views" ? "Views refreshed."
          : "Revision sent back for review.",
      );
      void load();
    } else {
      setError(r.message);
      toast.error(r.message);
    }
  }

  if (detail === null) return <PageSkeleton />;
  if (detail === "error") {
    return <StateBlock kind="error" title="Could not load that submission"
      action={<Button size="sm" onClick={() => onBack()}>Back</Button>} />;
  }

  const s = detail.submission;
  const openUrl = postOpenUrl({
    platform: s.platform,
    postId: s.post_id,
    submittedUrl: s.submitted_url,
    canonicalUrl: s.canonical_url,
  });
  const canRevise = s.state === "REVISION_REQUESTED" || s.state === "SUBMITTED";
  const approved = s.state === "APPROVED";
  const canWithdraw = !["WITHDRAWN", "REPLACED", "REJECTED"].includes(s.state);

  return (
    <div style={{ display: "grid", gap: space(4) }}>
      <div><Button size="sm" onClick={() => onBack(s.campaign_id)}>← Back to this campaign</Button></div>

      <PageHeader eyebrow={s.campaign_name ?? "Campaign"} title="Your submission"
        description="Where this post is in review, how many views it has, and when you can claim."
        actions={<StateBadge value={s.state} />} />

      {error ? <FormError message={error} /> : null}

      <EarningStatusCard
        earning={s.earning ?? null}
        currency={(s.earning?.currency as "GBP" | "USD" | "EUR") ?? "GBP"}
        busy={busy}
        onRefresh={() => void act("refresh-views")}
        onClaim={() => void act("claim-earning")}
      />

      <ModerationNote state={s.moderation_state} />

      <Card padding={18} style={{ display: "grid", gap: space(2) }}>
        <Eyebrow>The post</Eyebrow>
        {s.state === "PENDING_VERIFICATION" ? (
          <p style={{ margin: 0, fontSize: fontSize.body, lineHeight: 1.6 }}>
            We are following your short link to work out which post it points at. This
            usually takes a few minutes and nothing is needed from you.
          </p>
        ) : null}
        <p style={{ margin: 0, fontSize: fontSize.body }}>
          <a href={openUrl} target="_blank" rel="noreferrer noopener"
             style={{ color: "var(--accent)" }}>
            {openUrl}
          </a>
        </p>
        {s.canonical_url && s.canonical_url !== s.submitted_url ? (
          <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--muted)" }}>
            You submitted <code style={{ fontFamily: "var(--font-mono)" }}>{s.submitted_url}</code>.
            That is the same post.
          </p>
        ) : null}
        <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--muted)" }}>
          Availability: {s.availability.replaceAll("_", " ").toLowerCase()}
          {s.availability_checked_at
            ? ` - last checked ${new Date(s.availability_checked_at).toISOString().slice(0, 16).replace("T", " ")}`
            : " - not checked yet"}
          {s.availability === "FETCH_FAILED"
            ? ". A check that could not reach the post is not the same as the post being gone." : ""}
        </p>
      </Card>

      {detail.reviews.length > 0 ? (
        <Card padding={18} style={{ display: "grid", gap: space(2) }}>
          <Eyebrow>What the brand said</Eyebrow>
          {detail.reviews.map((rv, i) => (
            <div key={`${rv.revision}-${i}`} style={{
              display: "grid", gap: 4, paddingBottom: 10,
              borderBottom: i < detail.reviews.length - 1 ? "1px solid var(--line-soft)" : "none",
            }}>
              <div style={{ display: "flex", gap: space(2), alignItems: "center" }}>
                <StateBadge value={rv.decision} />
                <span style={{ fontSize: fontSize.micro, color: "var(--faint)" }}>
                  revision {rv.revision} · {new Date(rv.created_at).toISOString().slice(0, 10)}
                </span>
              </div>
              {rv.reason ? (
                <p style={{ margin: 0, fontSize: fontSize.body, lineHeight: 1.6 }}>{rv.reason}</p>
              ) : null}
            </div>
          ))}
        </Card>
      ) : null}

      {canRevise ? (
        <Card padding={18} style={{ display: "grid", gap: space(2), maxWidth: 620 }}>
          <Eyebrow>Revise</Eyebrow>
          <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--muted)", lineHeight: 1.6 }}>
            A revision keeps the same post. Your views and earning eligibility are not reset -
            this is a correction to what the brand reviews, not a new submission.
          </p>
          <TextInput label="Caption" value={caption}
            onChange={(e) => setCaption(e.currentTarget.value)} />
          <TextInput label="Notes for the brand" value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)} />
          <div>
            <Button variant="primary" size="sm" disabled={busy}
              onClick={() => void act("revise", { caption, notes })}>
              {busy ? "Sending…" : "Send revision"}
            </Button>
          </div>
        </Card>
      ) : null}

      {canWithdraw ? (
        <Card padding={18} style={{ display: "grid", gap: space(2) }}>
          <Eyebrow>Withdraw</Eyebrow>
          <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--muted)", lineHeight: 1.6 }}>
            {approved
              ? "The brand has approved this post. It cannot be withdrawn."
              : "Withdrawing releases the post, so you or anyone else could submit it to a different campaign afterwards."}
          </p>
          <div>
            <Button
              size="sm"
              disabled={busy || approved}
              onClick={() => { if (!approved) void act("withdraw"); }}
            >
              Withdraw this submission
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

/* --------------------------------------------------------------- list */

export function SubmissionsScreen({
  onOpen, campaignId, embedded = false, emptyAction, reloadKey = 0,
}: {
  onOpen: (id: string) => void;
  campaignId?: string;
  embedded?: boolean;
  emptyAction?: ReactNode;
  reloadKey?: number;
}) {
  const [rows, setRows] = useState<Submission[] | null | "error">(null);

  useEffect(() => {
    void api.get<{ submissions: Submission[] }>("/submissions")
      .then((r) => {
        if (!r.ok) { setRows("error"); return; }
        const list = r.data.submissions ?? [];
        setRows(campaignId ? list.filter((s) => s.campaign_id === campaignId) : list);
      });
  }, [campaignId, reloadKey]);

  return (
    <>
      {embedded ? (
        <p style={{ margin: "0 0 4px", fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
          Posts you have sent on this campaign, and where each one stands.
        </p>
      ) : (
        <PageHeader
          eyebrow="Submissions"
          title="Your submissions"
          description="Every post you have submitted, and where each one stands. New posts are submitted from the campaign they belong to."
        />
      )}

      {rows === null ? <RibbonSkeleton count={4} />
        : rows === "error" ? <StateBlock kind="error" title="Could not load your submissions" />
        : rows.length === 0
          ? <StateBlock kind="empty" title="Nothing submitted yet"
              detail={embedded
                ? "Publish the post on the platform first, then submit the link from the Campaign tab. Views stay blank until a post is measured - never shown as zero."
                : "Publish your post on the platform first, then submit the link from that campaign in My Campaigns. Views stay blank until a post is measured - never shown as zero."}
              action={emptyAction ?? (embedded
                ? undefined
                : <LinkButton href="/work" variant="primary" size="sm">Open My Campaigns</LinkButton>)} />
          : (
            <div style={{ display: "grid", gap: 10 }}>
              {rows.map((s) => (
                <div key={s.id} className="cm-work-ribbon cm-list-ribbon" style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(200px, 1.4fr) minmax(160px, 1fr) auto auto",
                  alignItems: "center",
                  gap: 16,
                  padding: "14px 16px",
                  border: "1px solid var(--line)",
                  borderRadius: 14,
                  background: "var(--surface)",
                }}>
                  <div style={{ display: "grid", gap: 4, minWidth: 0 }}>
                    <button type="button" onClick={() => onOpen(s.id)} style={{
                      background: "none", border: "none", padding: 0, cursor: "pointer",
                      color: "var(--text)", font: "inherit", textAlign: "left",
                      fontSize: fontSize.bodyLg, fontWeight: 650,
                    }}>
                      {embedded
                        ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            <PlatformIcon id={s.platform} size={18} />
                            {platformMeta(s.platform).label}
                          </span>
                        : (s.campaign_name ?? "Campaign")}
                    </button>
                    <span style={{
                      fontSize: fontSize.label, color: "var(--muted)",
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}>
                      {embedded ? `Revision ${s.current_revision}` : (
                        <>
                          <PlatformIcon id={s.platform} size={16} />
                          {s.platform} · revision {s.current_revision}
                        </>
                      )}
                    </span>
                  </div>
                  <span style={{ fontSize: fontSize.body, color: "var(--muted)" }}>
                    {new Date(s.submitted_at).toLocaleDateString(undefined, {
                      day: "numeric", month: "short", year: "numeric",
                    })}
                  </span>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    {s.earning?.views != null
                      ? <Badge tone={Number(s.earning.views) >= 1000 ? "ok" : "neutral"}>
                          {formatViews(s.earning.views)} views
                        </Badge>
                      : null}
                    {s.earning?.can_claim ? <Badge tone="ok">ready to claim</Badge> : null}
                    {s.earning?.claim_state === "REQUESTED" ? <Badge tone="process">claim with brand</Badge> : null}
                    {s.moderation_state !== "CLEAR"
                      ? <Badge tone="warn">platform {s.moderation_state.toLowerCase()}</Badge> : null}
                    <StateBadge value={s.state} />
                  </div>
                  <Button size="sm" variant="primary" onClick={() => onOpen(s.id)}>View</Button>
                </div>
              ))}
            </div>
          )}
    </>
  );
}
